import type { WorkspaceLayoutPreset } from 'generated/graphql';
import {
  applyWorkspaceLayoutPresetEvent,
  dispatchExtensionEvent,
} from 'domains/extension/extensionEventCreators';
import { logger } from 'modules/logger';
import { getWorkspaceLayoutSnapshot } from './getWorkspaceLayoutSnapshot';
import { toExtensionDisplayIndexes } from './utils';

// Resolves to true once the layout was sent to the extension
export async function applyWorkspaceLayoutPreset(
  preset: Pick<WorkspaceLayoutPreset, 'id' | 'name' | 'windows'>
): Promise<boolean> {
  if (preset.windows.length === 0) {
    logger.warn(
      `[WorkspaceLayoutManager] Skipping workspace layout preset ${preset.name} (${preset.id}) because it has no windows`
    );
    return false;
  }

  const snapshot = await getWorkspaceLayoutSnapshot().catch((error: unknown) => {
    logger.warn(
      `[WorkspaceLayoutManager] Skipping workspace layout preset ${preset.id} because displays could not be read`,
      error
    );
    return null;
  });

  if (snapshot == null) return false;

  const { windows, missingDisplayCount } = toExtensionDisplayIndexes({
    windows: preset.windows,
    displays: snapshot.displays,
  });

  const remappedCount = windows.filter(
    (windowLayout, index) => windowLayout.displayIndex !== preset.windows[index].displayIndex
  ).length;

  logger.info('[WorkspaceLayoutManager] Applying workspace layout preset', {
    presetId: preset.id,
    windowCount: windows.length,
    remappedCount,
    missingDisplayCount,
  });

  dispatchExtensionEvent(applyWorkspaceLayoutPresetEvent({ preset: { windows } }));
  return true;
}
